import { UserOrganizationResponse } from '@cdaas/api-clients/dist/asset-user-openapi';

import { NUMBER_OF_PRODUCT_PER_PAGE } from './productUtils';
import { FilterProductsParams } from '../types';
import history from '../history';

export const buildQueryString = (filterParams: FilterProductsParams): string => {
  const params: Record<string, string> = {};
  if (filterParams.paging?.currentPage && filterParams.paging?.currentPage > 1) {
    params.page = `${filterParams.paging!.currentPage - 1}`;
  }
  if (filterParams.paging?.numberOfProductsPerPage !== NUMBER_OF_PRODUCT_PER_PAGE) {
    params.size = `${filterParams.paging!.numberOfProductsPerPage}`;
  }
  if (filterParams?.brands?.length) {
    params.brand = `${filterParams.brands.join(',')}`;
  }
  if (filterParams?.textSearch) {
    params.name = `${filterParams.textSearch}`;
  }
  if (filterParams?.sorting?.value) {
    const [sort, direction] = filterParams.sorting.value.split('_');
    params.sort = `${sort}`;
    params.direction = `${direction}`;
  }
  const searchParamsString = new URLSearchParams(params).toString();
  return searchParamsString ? `?${searchParamsString}` : '';
};

export const getTicketURI = (language?: UserOrganizationResponse['locale']['language']) => {
  switch (language?.split('-')[0]) {
    case 'no':
      return process.env.REACT_APP_TICKET_URI_NO;
    case 'se':
      return process.env.REACT_APP_TICKET_URI_SE;
    case 'fi':
      return process.env.REACT_APP_TICKET_URI_FI;
    default:
      return process.env.REACT_APP_TICKET_URI;
  }
};

/**
 * Generic function to update query params in the URL. Does not support values that are arrays.
 * @param pathname pathname as string
 * @param searchParams Record/Object with query params for updating into URL
 * @param replaceOrPush for React router to use replace or push method
 */
export const updateQueryParams = (pathname: string, searchParams: Record<string, string | number | boolean>, replaceOrPush: 'replace' | 'push') => {
  const convertedToStrings: Record<string, string> = {};
  for (const key of Object.keys(searchParams)) {
    convertedToStrings[key] = searchParams[key].toString();
  }
  replaceOrPush === 'replace' && history.replace({ pathname, search: `?${new URLSearchParams(convertedToStrings).toString()}` });
  replaceOrPush === 'push' && history.push({ pathname, search: `?${new URLSearchParams(convertedToStrings).toString()}` });
};

/**
 * Generic function to get query params from any URL. Does not support params that are arrays.
 * @param searchQuery usually location. Search in URLSearchParams
 * @param paramsConfig configuration for getting and parsing query params
 * @returns Record/Object parsed with given configuration
 */
export const getQueryParams = (
  searchQuery: URLSearchParams,
  paramsConfig: {
    name: string;
    type: 'string' | 'number' | 'boolean';
    defaultValue?: string | number | boolean;
    allowedValues?: string[] | number[];
  }[],
): Record<string, string | number | boolean> => {
  const final: Record<string, string | number | boolean> = {};
  for (const config of paramsConfig) {
    // Get from query params
    let queryParam = searchQuery.get(config.name) ?? config.defaultValue;
    // Convert to number and check
    if (config.type === 'number') {
      queryParam = Number.isInteger(+queryParam!) ? +queryParam! : config.defaultValue;
    }
    // Convert to boolean and check
    if (config.type === 'boolean') {
      queryParam = queryParam === 'true' ? true : queryParam === 'false' ? false : config.defaultValue;
    }
    // Check if exists in allowed values. If exists then push to array and continue iterating
    if (config.allowedValues) {
      // Cast to (string | number)[] so that typescript is happy
      const exists = (config.allowedValues as (string | number)[]).find((value: string | number) => value === queryParam);
      if (exists) final[config.name] = queryParam!;
      continue;
    }
    // Push to array if valid
    if (queryParam !== null && queryParam !== undefined) {
      final[config.name] = queryParam;
    }
  }
  return final;
};
